'use client';
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import { Input, type InputProps } from './Input';
import { IconCalendar, IconChevronLeft, IconChevronRight } from '@repo/svgs';
import { cn } from '@/lib/cn';
import { useIsMobile } from '@/hooks/useDeviceType';

export interface DateInputProps
  extends Omit<InputProps, 'type' | 'onChange' | 'value' | 'defaultValue' | 'onClick'> {
  /** ISO date value in YYYY-MM-DD */
  value: string;
  /** Called with ISO date in YYYY-MM-DD */
  onChange: (next: string) => void;
  min?: string;
  max?: string;
}

type CalendarHeaderProps = {
  date: Date;
  decreaseMonth: () => void;
  increaseMonth: () => void;
  decreaseYear: () => void;
  increaseYear: () => void;
  changeYear: (year: number) => void;
  changeMonth: (month: number) => void;
  prevMonthButtonDisabled: boolean;
  nextMonthButtonDisabled: boolean;
  prevYearButtonDisabled: boolean;
  nextYearButtonDisabled: boolean;
};

const toLocalDate = (value?: string) => {
  if (!value) return null;
  const trimmed = value.trim();
  const [datePart] = trimmed ? trimmed.split('T') : [undefined];
  if (!datePart) return null;
  const [yearStr, monthStr, dayStr] = datePart.split('-');
  if (!yearStr || !monthStr || !dayStr) return null;
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
  const date = new Date(year, month - 1, day, 12, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toIsoDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

type DateInputFieldProps = Omit<InputProps, 'onClick'> & {
  /**
   * `react-datepicker` provides an `onClick` handler to open the calendar popover.
   * We route it through the Input "field" wrapper so the entire pill is clickable.
   */
  onClick?: React.MouseEventHandler<HTMLElement>;
};

const DateInputField = React.forwardRef<HTMLInputElement, DateInputFieldProps>(
  function DateInputField({ onClick, disabled, rightIcon, className, ...props }, ref) {
    const inputRef = useRef<HTMLInputElement>(null);
    const handleClick: React.MouseEventHandler<HTMLDivElement> = event => {
      if (disabled) return;
      event.preventDefault();
      inputRef.current?.focus();
      onClick?.(event as unknown as React.MouseEvent<HTMLElement>);
    };

    return (
      <Input
        {...props}
        ref={node => {
          inputRef.current = node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        type="text"
        readOnly
        disabled={disabled}
        className={cn('cursor-pointer caret-transparent relative', className)}
        fieldOnClick={handleClick}
        rightIcon={rightIcon ?? <IconCalendar className="h-4 w-4 icon-primary" />}
      />
    );
  },
);

export function DateInput({
  value,
  onChange,
  placeholder = 'Select date',
  disabled,
  min,
  max,
  rightIcon,
  ...props
}: DateInputProps) {
  const { onBlur, ...inputProps } = props;
  const instanceId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const selected = useMemo(() => toLocalDate(value), [value]);
  const minDate = useMemo(() => toLocalDate(min), [min]);
  const maxDate = useMemo(() => toLocalDate(max), [max]);
  const openToDate = useMemo(() => {
    if (!minDate) return selected ?? undefined;
    if (!selected) return minDate;
    return selected < minDate ? minDate : selected;
  }, [selected, minDate]);
  const popperContainer = useCallback(({ children }: { children?: React.ReactNode }) => {
    if (typeof document === 'undefined') return <>{children ?? null}</>;
    return createPortal(children ?? null, document.body);
  }, []);

  const openCalendar = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
    window.dispatchEvent(new CustomEvent('oi-date-input-open', { detail: { id: instanceId } }));
  }, [disabled, instanceId]);

  useEffect(() => {
    const handleOpen = (event: Event) => {
      const custom = event as CustomEvent<{ id?: string }>;
      if (custom.detail?.id !== instanceId) setIsOpen(false);
    };

    window.addEventListener('oi-date-input-open', handleOpen as EventListener);
    return () => window.removeEventListener('oi-date-input-open', handleOpen as EventListener);
  }, [instanceId]);

  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled]);

  const renderHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    changeYear,
    changeMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }: CalendarHeaderProps) => {
    const baseYear = date.getFullYear();
    const minYear = minDate ? minDate.getFullYear() : baseYear - 100;
    const maxYear = maxDate ? maxDate.getFullYear() : baseYear + 100;
    const minMonth = minDate && baseYear === minYear ? minDate.getMonth() : 0;
    const maxMonth = maxDate && baseYear === maxYear ? maxDate.getMonth() : 11;
    const years: number[] = [];

    for (let year = minYear; year <= maxYear; year += 1) {
      years.push(year);
    }

    return (
      <div className="flex items-center justify-between gap-2 pb-2 bg-primary">
        <button
          type="button"
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-tertiary transition hover:bg-secondary hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          <IconChevronLeft className="h-4 w-4 icon-primary" />
        </button>

        <div className="flex items-center justify-center gap-2 flex-1">
          <span className="text-body-2-high text-primary">
            {date.toLocaleDateString(undefined, { month: 'long' })}
          </span>
          <div className="rounded-full cursor-pointer border border-secondary bg-primary px-2 py-1  text-body-2 text-primary">
            <select
              value={baseYear}
              onChange={event => {
                const nextYear = Number(event.target.value);
                changeYear(nextYear);
                if (nextYear === minYear && date.getMonth() < minMonth) {
                  changeMonth(minMonth);
                } else if (nextYear === maxYear && date.getMonth() > maxMonth) {
                  changeMonth(maxMonth);
                }
              }}
            >
              {years.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          type="button"
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-tertiary transition hover:bg-secondary hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          <IconChevronRight className="h-4 w-4 icon-primary" />
        </button>
      </div>
    );
  };

  return (
    <div>
      <DatePicker
        selected={selected}
        onChange={(date: Date | [Date | null, Date | null] | null) => {
          const nextDate = Array.isArray(date) ? date[0] : date;
          onChange(nextDate ? toIsoDate(nextDate) : '');
          setIsOpen(false);
        }}
        disabled={disabled}
        closeOnScroll={true}
        formatWeekDay={name => name.slice(0, 3)}
        dateFormat="MMM dd, yyyy"
        placeholderText={placeholder}
        popperPlacement="bottom-start"
        showPopperArrow={false}
        openToDate={openToDate as Date}
        open={isOpen}
        onBlur={onBlur ?? (() => {})}
        onInputClick={openCalendar}
        onClickOutside={() => setIsOpen(false)}
        onCalendarClose={() => setIsOpen(false)}
        popperContainer={popperContainer}
        calendarClassName="oi-datepicker"
        popperClassName="oi-datepicker-popper"
        renderCustomHeader={renderHeader}
        {...(minDate ? { minDate } : {})}
        {...(maxDate ? { maxDate } : {})}
        customInput={
          <DateInputField
            {...inputProps}
            disabled={disabled}
            placeholder={placeholder}
            rightIcon={rightIcon}
          />
        }
      />
    </div>
  );
}
